/**
 * Guard: a hybrid preview must be computed against the clause's own live paragraph.
 *
 * Table-row MCC stores OOXML document-order indices. `previewHybridOps` / `applyViaHybridEngine`
 * index `body.paragraphs`, which skips some table cells — so passing the OOXML index reads a
 * *different* clause. The card then flashes the AI suggestion and, after debounce, jumps to that
 * other paragraph's redline.
 */

function foldWs(text: string): string {
  return (text || '').replace(/\s+/g, ' ').trim();
}

/** How many of {prefix, mid, suffix} of `needle` appear in `haystack`. */
function distinctiveHits(haystack: string, needle: string): number {
  const a = foldWs(haystack);
  const b = foldWs(needle);
  if (!a || !b) return 0;
  if (a === b) return 3;
  if (b.length >= 40 && a.includes(b)) return 3;
  if (a.length >= 40 && b.includes(a)) return 3;
  const window = Math.min(64, b.length);
  if (window < 40) return a.includes(b) || b.includes(a) ? 2 : 0;
  let hits = 0;
  if (a.includes(b.slice(0, window))) hits += 1;
  const midStart = Math.max(0, Math.floor((b.length - window) / 2));
  if (a.includes(b.slice(midStart, midStart + window))) hits += 1;
  if (a.includes(b.slice(-window))) hits += 1;
  return hits;
}

/**
 * True when `baselineVisible` (live Word paragraph text) is the clause we are previewing,
 * not a neighbour table cell that happens to share `body.paragraphs[ooxmlIndex]`.
 *
 * Requires two distinctive slices (or a full containment) so a shared legal prefix like
 * "בכפוף להסכמת הקונה…" is not enough to accept the wrong cell.
 */
export function hybridPreviewBaselineBelongsToClause(
  baselineVisible: string,
  clausePlainText: string,
  cleanTarget?: string,
): boolean {
  if (distinctiveHits(baselineVisible, clausePlainText) >= 2) return true;
  if (cleanTarget && distinctiveHits(baselineVisible, cleanTarget) >= 2) return true;
  return false;
}

/** True when `source` has a window of text that does not appear in `other`. */
function hasSliceNotInOther(source: string, other: string, window = 40): boolean {
  const a = foldWs(source);
  const b = foldWs(other);
  if (!a) return false;
  if (a.length < window) return !b.includes(a);
  const step = Math.max(8, Math.floor(window / 2));
  for (let i = 0; i + window <= a.length; i += step) {
    if (!b.includes(a.slice(i, i + window))) return true;
  }
  return !b.includes(a.slice(-window));
}

/**
 * After a successful hybrid apply, `range.text` still includes deleted runs, so the live
 * paragraph is longer than both the original and the clean target and still contains
 * distinctive slices of each. Re-diffing that baseline keeps our insertion as an existing
 * revision and the preview paints it red (counterparty) instead of blue.
 *
 * Only replacement suggestions qualify (the target must introduce wording the original did
 * not have). A pure shorten would otherwise look "already applied" before Submit.
 */
export function hybridPreviewLooksAlreadyApplied(
  baselineVisible: string,
  originalClause: string,
  cleanTarget: string,
): boolean {
  const a = foldWs(baselineVisible);
  const o = foldWs(originalClause);
  const t = foldWs(cleanTarget);
  if (!a || !o || !t) return false;
  if (a.length < o.length + 8) return false;
  if (a.length < t.length + 8) return false;
  if (a.length <= Math.max(o.length, t.length) + 16) return false;
  if (distinctiveHits(a, o) < 2) return false;
  if (distinctiveHits(a, t) < 2) return false;
  if (!hasSliceNotInOther(t, o)) return false;
  return true;
}
